import { Region, CardBreakdown, CardDenomination, RangeBracket, Product, ProductVariant, Language } from '../types';

// Round to nearest 5: 1113→1115, 4329→4330, 1112→1110
export const roundTo5 = (n: number): number => Math.round(n / 5) * 5;

// Format price in rubles (rounded to nearest 0 or 5)
export const formatPrice = (price: number): string => {
  return `${roundTo5(price).toLocaleString('ru-RU')} ₽`;
};

// Format discount
export const formatDiscount = (discount: number): string => {
  return `-${discount}%`;
};

// Get regional currency symbol
export const getRegionalCurrency = (region: Region): string => {
  const currencies: Record<Region, string> = {
    TR: 'TL',
    IN: 'INR',
    UA: 'UAH',
  };
  return currencies[region];
};

// Format regional price (e.g., "1 727 TL")
export const formatRegionalPrice = (amount: number, region: Region): string => {
  const rounded = Math.ceil(amount);
  return `${rounded.toLocaleString('ru-RU')} ${getRegionalCurrency(region)}`;
};

// Convert ruble price to regional currency
export const toRegionalPrice = (priceRub: number, exchangeRate: number): number => {
  return priceRub / exchangeRate;
};

// Find optimal card combination
// Goal: minimum overpay, then minimum number of cards
export interface OptimalCardsResult {
  cards: CardBreakdown[];
  totalNominal: number;      // sum in regional currency
  totalRub: number;          // sum in rubles
  orderNominal: number;      // order total in regional currency
  overpay: number;           // overpay in regional currency
}

// Эффективный курс по регионам: override товара перекрывает глобальный курс.
export const effectiveRates = (
  exchangeRates: Record<string, number>,
  rateOverrides?: Record<string, number> | null,
): Record<string, number> => {
  const overrides = rateOverrides && typeof rateOverrides === 'object' ? rateOverrides : {};
  return { ...exchangeRates, ...overrides };
};

// Курс диапазона по региональному номиналу. null = нет подходящего диапазона.
export const pickBracketRate = (
  brackets: RangeBracket[] | null | undefined,
  regional: number,
): number | null => {
  if (!Array.isArray(brackets) || brackets.length === 0) return null;
  const valid = brackets
    .filter((b) => typeof b.rate === 'number' && b.rate > 0)
    .map((b) => ({
      from: Number(b.from) || 0,
      to: b.to === null || b.to === undefined ? Infinity : Number(b.to),
      rate: Number(b.rate),
    }))
    .sort((a, b) => a.from - b.from);
  for (const b of valid) {
    if (regional <= b.to) return b.rate;
  }
  return null;
};

type PricingCfg = {
  exchangeRates?: Record<string, number> | null;
  rangePricing?: Partial<Record<string, RangeBracket[]>> | null;
  codesRates?: Partial<Record<string, number>> | null;
};

// Курс каталога (режим account) для отображения. undefined = нет контекста курса
// (тогда используется сохраненная рублевая цена варианта).
// Приоритет: override товара → диапазон по регионалке → плоский курс.
export const accountRate = (
  region: string,
  regional: number,
  rateOverrides: Record<string, number> | null | undefined,
  cfg: PricingCfg | null | undefined,
): number | undefined => {
  if (!cfg) return undefined;
  const ov = rateOverrides && typeof rateOverrides === 'object' ? rateOverrides[region] : undefined;
  if (typeof ov === 'number' && ov > 0) return ov;
  const br = pickBracketRate(cfg.rangePricing?.[region], regional);
  if (typeof br === 'number' && br > 0) return br;
  const flat = cfg.exchangeRates?.[region];
  return typeof flat === 'number' && flat > 0 ? flat : undefined;
};

// Эффективный курс товара (для корзины/заказа), всегда число.
// Приоритет: override → (codes) курс выдачи кодов → (account) диапазон → плоский курс.
export const resolveRate = (
  region: string,
  regional: number,
  mode: 'account' | 'codes',
  rateOverrides: Record<string, number> | null | undefined,
  cfg: PricingCfg | null | undefined,
): number => {
  const ov = rateOverrides && typeof rateOverrides === 'object' ? rateOverrides[region] : undefined;
  if (typeof ov === 'number' && ov > 0) return ov;
  if (mode === 'codes') {
    const cr = cfg?.codesRates?.[region];
    if (typeof cr === 'number' && cr > 0) return cr;
    return cfg?.exchangeRates?.[region] || 1;
  }
  const br = pickBracketRate(cfg?.rangePricing?.[region], regional);
  if (typeof br === 'number' && br > 0) return br;
  return cfg?.exchangeRates?.[region] || 1;
};

export const findOptimalCards = (
  orderTotal: number,          // in regional currency
  denominations: CardDenomination[]
): OptimalCardsResult => {
  // Sort denominations descending by nominal
  const sorted = [...denominations].sort((a, b) => b.nominal - a.nominal);

  // Use greedy approach: try to cover the total with largest cards first
  // Then add the smallest card that covers the remainder
  type BestResult = { cards: Map<number, { count: number; priceRub: number }>; total: number; totalRub: number };
  let best: BestResult | null = null;

  // Recursive search with pruning (limited depth due to small number of denominations)
  const search = (
    remaining: number,
    idx: number,
    current: Map<number, { count: number; priceRub: number }>,
    currentTotal: number,
    currentRub: number
  ) => {
    if (remaining <= 0) {
      const overpay = currentTotal - orderTotal;
      if (!best || overpay < best.total - orderTotal ||
        (overpay === best.total - orderTotal && getTotalCards(current) < getTotalCards(best.cards))) {
        best = { cards: new Map(current), total: currentTotal, totalRub: currentRub };
      }
      return;
    }

    if (idx >= sorted.length) return;

    const denom = sorted[idx];
    const maxCount = Math.ceil(remaining / denom.nominal);

    // Try from max count down to 0
    for (let count = maxCount; count >= 0; count--) {
      const newRemaining = remaining - denom.nominal * count;
      const newTotal = currentTotal + denom.nominal * count;
      const newRub = currentRub + denom.priceRub * count;

      // Prune: if we already have a better solution
      if (best && newTotal - orderTotal > best.total - orderTotal) continue;

      if (count > 0) {
        current.set(denom.nominal, { count, priceRub: denom.priceRub });
      }

      if (newRemaining <= 0) {
        // We've covered the order
        const overpay = newTotal - orderTotal;
        if (!best || overpay < best.total - orderTotal ||
          (overpay === best.total - orderTotal && getTotalCards(current) < getTotalCards(best.cards))) {
          best = { cards: new Map(current), total: newTotal, totalRub: newRub };
        }
      } else {
        search(newRemaining, idx + 1, current, newTotal, newRub);
      }

      if (count > 0) {
        current.delete(denom.nominal);
      }
    }
  };

  search(orderTotal, 0, new Map(), 0, 0);

  if (!best) {
    // Fallback: use the largest denomination
    const largest = sorted[0];
    const count = Math.ceil(orderTotal / largest.nominal);
    return {
      cards: [{ denomination: largest.nominal, count, priceRub: largest.priceRub }],
      totalNominal: largest.nominal * count,
      totalRub: largest.priceRub * count,
      orderNominal: orderTotal,
      overpay: largest.nominal * count - orderTotal,
    };
  }

  const result = best as BestResult;
  const cards: CardBreakdown[] = [];
  // Sort by denomination descending for display
  const sortedEntries: Array<[number, { count: number; priceRub: number }]> = [];
  result.cards.forEach((val, key) => sortedEntries.push([key, val]));
  sortedEntries.sort((a, b) => b[0] - a[0]);
  for (const [denom, { count, priceRub }] of sortedEntries) {
    cards.push({ denomination: denom, count, priceRub });
  }

  return {
    cards,
    totalNominal: result.total,
    totalRub: result.totalRub,
    orderNominal: orderTotal,
    overpay: result.total - orderTotal,
  };
};

const getTotalCards = (cards: Map<number, { count: number; priceRub: number }>): number => {
  let total = 0;
  cards.forEach(({ count }) => { total += count; });
  return total;
};

// Legacy card breakdown (kept for compatibility)
export const calculateCardBreakdown = (
  totalAmount: number,
  region: Region
): CardBreakdown[] => {
  const denominations = region === 'TR' ? [2000, 1000, 500] : [5000, 2000, 1000];
  const breakdown: CardBreakdown[] = [];
  let remaining = totalAmount;

  for (const denom of denominations) {
    if (remaining >= denom) {
      const count = Math.floor(remaining / denom);
      breakdown.push({ denomination: denom, count, priceRub: denom });
      remaining -= denom * count;
    }
  }

  return breakdown;
};

// Format date (accepts DD.MM.YYYY or ISO formats)
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const parts = dateString.split('.');
  if (parts.length === 3) {
    return dateString;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Get region name
export const getRegionName = (region: Region): string => {
  const names: Record<Region, string> = {
    TR: 'Турция',
    IN: 'Индия',
    UA: 'Украина (СНГ)',
  };
  return names[region];
};

// Подпись платформы предложения в карточке, корзине и плитках поиска:
// у Sony версия «PS4, PS5» — одна покупка на обе консоли.
export const formatPlatform = (platform: string | null | undefined): string => {
  const p = (platform || '').trim();
  if (!p) return '';
  const parts = p.split(/\s*[,/&+]\s*|\s+и\s+|\s+and\s+/i).filter(Boolean);
  return parts.length > 1 ? parts.join(' и ') : p;
};

// Плитки поиска: у игры с версиями «для PS4» и «для PS4/PS5» в регионе покупателя —
// по плитке на версию, как издания в PS Store (клиент: «должны быть издания для PS4,
// для PS4/PS5, Deluxe»). Остальным карточкам — одна плитка без предложения.
export const splitByPlatform = (
  product: Product,
  region: Region | null | undefined,
): Array<{ product: Product; variant?: ProductVariant }> => {
  if (product.category !== 'game' || !region || !product.variants) return [{ product }];
  const byPlatform = new Map<string, ProductVariant>();
  for (const v of product.variants) {
    if (!v.available || v.region !== region || v.price <= 0) continue;
    const key = v.platform || '';
    const cur = byPlatform.get(key);
    if (!cur || v.price < cur.price) byPlatform.set(key, v);
  }
  if (byPlatform.size < 2) return [{ product }];
  return Array.from(byPlatform.values())
    .sort((a, b) => a.price - b.price)
    .map((variant) => ({ product, variant }));
};

// Get platform name
export const getPlatformName = (platform: string): string => {
  return platform;
};

// Get language name
export const getLanguageName = (language: string): string => {
  const names: Record<string, string> = {
    ru_subtitles: 'Русские субтитры',
    ru_full: 'Полностью на русском',
  };
  return names[language] || language;
};

// Язык варианта приходит текстом («Русские субтитры», «С русскими субтитрами»,
// «Полностью на русском», «Без перевода») — сводим к кодам фильтра
export const languageKind = (text: string | null | undefined): Language | null => {
  if (!text) return null;
  if (/полностью/i.test(text)) return 'ru_full';
  if (/субтитр/i.test(text)) return 'ru_subtitles';
  return null;
};

// Get edition name
export const getEditionName = (edition: string): string => {
  const names: Record<string, string> = {
    standard: 'Standard Edition',
    deluxe: 'Deluxe Edition',
    ultimate: 'Ultimate Edition',
  };
  return names[edition] || edition;
};

// Truncate text
export const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};
